import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../../stores/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationDropdown } from '../../../components/feature/notification';
import { useSiteConfig } from '../../../hooks/useSiteConfig';

const NAV_ITEMS = [
  { key: 'dashboard',     label: '仪表盘',  icon: 'ri-dashboard-line',       path: '/' },
  { key: 'order-manage',  label: '订单管理', icon: 'ri-file-list-3-line',     path: '/orders' },
  { key: 'analytics',     label: '数据统计', icon: 'ri-bar-chart-2-line',     path: '/analytics' },
  { key: 'accounting',    label: '核算管理', icon: 'ri-calculator-line',      path: '/accounting' },
  { key: 'customers',     label: '客户管理', icon: 'ri-user-line',            path: '/customers' },
  { key: 'profile',       label: '个人中心', icon: 'ri-user-settings-line',   path: '/profile' },
];

// Mobile bottom nav only shows top 5 (profile accessible via drawer)
const BOTTOM_NAV_KEYS = ['dashboard', 'order-manage', 'analytics', 'accounting', 'customers'];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { profile, user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteName, logoUrl } = useSiteConfig();

  const activeTab = (() => {
    if (location.pathname.startsWith('/profile'))    return 'profile';
    if (location.pathname.startsWith('/customers'))  return 'customers';
    if (location.pathname.startsWith('/settings'))   return 'settings';
    if (location.pathname.startsWith('/orders'))     return 'order-manage';
    if (location.pathname.startsWith('/analytics'))  return 'analytics';
    if (location.pathname.startsWith('/accounting')) return 'accounting';
    return 'dashboard';
  })();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setDropdownOpen(false);
    setDrawerOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const goto = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const displayName = profile?.fullName || profile?.username || user?.username || user?.email?.split('@')[0] || '用户';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <>
      {/* ── Top bar ──────────────────────────────────────────────── */}
      <nav className="bg-[var(--color-primary-600)] border-b border-[var(--color-primary-700)] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">

            {/* Left: hamburger (mobile) + logo */}
            <div className="flex items-center gap-2 md:gap-8">
              {/* Hamburger - mobile only */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-white/70 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-menu-line text-xl" />
              </button>

              {/* Logo */}
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => goto('/login')}
              >
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-7 md:h-8 w-auto"
                />
                <span className="hidden sm:block text-sm md:text-base font-semibold text-white whitespace-nowrap">
                  {siteName}
                </span>
              </div>

              {/* Desktop nav tabs */}
              <div className="hidden md:flex items-center gap-1">
                {NAV_ITEMS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => goto(tab.path)}
                    className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-white text-[var(--color-primary-600)] shadow-sm'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${tab.icon} text-sm`} />
                    </div>
                    {tab.label}
                    {activeTab === tab.key && (
                      <span className="absolute -bottom-[1px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--color-primary-500)]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: notification + user */}
            <div className="flex items-center gap-2 md:gap-4">
              <NotificationDropdown />

              {/* User dropdown - desktop */}
              <div className="hidden md:block relative pl-4 border-l border-teal-500" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden ring-2 ring-amber-400">
                    {profile?.avatar
                      ? <img src={profile.avatar} alt="头像" className="w-full h-full object-cover scale-125" />
                      : <span>{avatarLetter}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-white">{displayName}</span>
                    <i className={`ri-arrow-down-s-line text-teal-100 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl border border-gray-100 py-1 z-50">
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{displayName}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{profile?.email || ''}</p>
                    </div>
                    <button onClick={() => { setDropdownOpen(false); goto('/profile'); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-3-line text-gray-400" /></div>
                      个人中心
                    </button>
                    <button onClick={() => { setDropdownOpen(false); goto('/settings'); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-gray-400" /></div>
                      系统设置
                    </button>
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line text-red-400" /></div>
                      退出登录
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile Drawer ────────────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-50 md:hidden"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Panel */}
          <div className="fixed top-0 left-0 w-72 h-full bg-white z-50 md:hidden flex flex-col shadow-xl">
            {/* Drawer header */}
            <div className="bg-teal-600 px-5 py-5 flex items-center gap-3">
              <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden shrink-0 ring-2 ring-amber-400">
                {profile?.avatar
                  ? <img src={profile.avatar} alt="头像" className="w-full h-full object-cover scale-125" />
                  : <span>{avatarLetter}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{displayName}</p>
                <p className="text-xs text-teal-200 truncate">{profile?.email || ''}</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-teal-200 hover:text-white cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Nav items */}
            <div className="flex-1 overflow-y-auto py-3">
              {NAV_ITEMS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => goto(tab.path)}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium transition-colors cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-600)] border-r-2 border-[var(--color-primary-500)]'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${tab.icon} text-base`} />
                  </div>
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--color-primary-500)]" />
                  )}
                </button>
              ))}
            </div>

            {/* Drawer footer */}
            <div className="border-t border-gray-100 py-3">
              <button
                onClick={() => goto('/settings')}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-settings-3-line text-gray-400 text-base" />
                </div>
                系统设置
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-red-400 text-base" />
                </div>
                退出登录
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Mobile Bottom Navigation ─────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-100 md:hidden">
        <div className="flex items-center justify-around h-16 px-1 safe-bottom">
          {NAV_ITEMS.filter(tab => BOTTOM_NAV_KEYS.includes(tab.key)).map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => goto(tab.path)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 transition-colors cursor-pointer ${
                  isActive ? 'text-[var(--color-primary-600)]' : 'text-gray-400'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center relative">
                  <i className={`${tab.icon} text-xl`} />
                  {isActive && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--color-primary-500)]" />
                  )}
                </div>
                <span className="text-[10px] font-medium leading-none whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
